













































































































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { mapState } from "vuex";

//Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";

// Enums
import { DataModules } from "@/store/modules/data";

// Types
import { IBrand, IBrandInvoiceSettingsForm } from "@/models/brands";
import { IState } from "@/store";
import { BrandConfigKeys } from "@/data/constants";

@Component({
  components: {
    "recurring-due-date-control": () =>
      import("@/components/app/global/invoices/recurringDueDateControl.vue"),
    "non-recurring-due-date-control": () =>
      import("@/components/app/global/invoices/nonRecurringDueDateControl.vue"),
    "invoice-generation-control": () =>
      import("@/components/app/global/invoices/invoiceGenerationControl.vue")
  },
  computed: {
    ...mapState({
      brandId(state: IState) {
        return state.brand.id;
      },
      brandScope(s, getters) {
        return getters[`data/${this.dataModule}/brandScope`](this.brandId);
      },
      brand(s, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule,
          scope: this.brandScope
        });
      }
    })
  }
})
export default class BrandInvoiceSettings extends Mixins(
  RequestPromises,
  HasValidation
) {
  // Getters
  public brand!: IBrand;
  public brandId!: IBrand["id"];
  public brandScope!: string;

  @Prop({ required: true })
  public isProcessing!: boolean;

  @Prop({ default: false })
  public dynamicSettingsLoading!: boolean;

  public form = {
    create_invoice_term: 0,
    credit_note_number: 0,
    credit_note_prefix: "",
    non_recurring_due_interval: 0,
    number: 0,
    payment_days_term: 0,
    prefix: ""
  } as IBrandInvoiceSettingsForm;
  public formClone = this.$_.clone(this.form);

  public isLoading = true;
  public isReloading = false;
  public requestError = false;

  public dataModule: string = DataModules.BRANDS;
  public savedBrandScope!: string;
  public brandInvoiceDetails: any;

  public created() {
    if (!this.$userCan("get_brand")) {
      this.isLoading = false;
    }
    this.savedBrandScope = this.brandScope;
    this.loadData();
  }

  public async loadData(ignoreStored = false) {
    try {
      const [brandInvoiceDetails] = await Promise.all([
        this.getBrandInvoiceNumberAndPrefix(ignoreStored),
        this.getBrandDetails(ignoreStored),
        this.getBrandConfig(ignoreStored)
      ]);

      this.brandInvoiceDetails = brandInvoiceDetails;
      this.fillForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData(true);
  }

  public getBrandDetails(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.BRANDS}/get`, {
      id: this.brandId,
      scope: this.brandScope,
      ignoreStored,
      vm: this
    });
  }

  public getBrandInvoiceNumberAndPrefix(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.BRANDS}/getInvoiceNumberAndPrefix`,
      { ignoreStored, vm: this }
    );
  }

  public getBrandConfig(ignoreStored = false) {
    return this.$store.dispatch("brand/getConfig", {
      brandId: this.brandId,
      ignoreStored,
      keys: [BrandConfigKeys.INVOICE_CREDIT_NOTE_NUMBER_SEPARATE]
    });
  }

  public fillForm() {
    this.form = (this.$_.pick(
      this.$_.merge({}, this.form, this.brand, this.brandInvoiceDetails),
      this.$_.intersection(
        this.$_.keys(
          this.$_.merge({}, this.form, this.brand, this.brandInvoiceDetails)
        ),
        this.$_.keys(this.form)
      )
    ) as any) as IBrandInvoiceSettingsForm;
    this.formClone = this.$_.clone(this.form);
  }

  public get formIsValid() {
    if (this.$_.isEqual(this.form, this.formClone)) return false;
    return true;
  }

  public get hasSeparateCreditNoteSequence() {
    return (
      this.$store.getters["brand/config"][
        BrandConfigKeys.INVOICE_CREDIT_NOTE_NUMBER_SEPARATE
      ] ?? false
    );
  }

  public save() {
    return new Promise<void>((resolve, reject) => {
      Promise.all([
        this.updateInvoiceAutomationSettings(),
        this.updateInvoiceNumberSettings()
      ])
        .then(() => {
          this.formClone = this.$_.clone(this.form);
          this.$emit("predefinedSettingsAreChanged", false);
          resolve();
        })
        .catch(error => {
          this.$store.dispatch("api/handleValidationError", {
            error,
            vm: this,
            showToast: false
          });
          reject(error);
        });
    });
  }

  public revertToInitialState() {
    this.fillForm();
  }

  public updateInvoiceAutomationSettings() {
    const newAutomationValues = this.getNewValues([
      "create_invoice_term",
      "non_recurring_due_interval",
      "payment_days_term"
    ]);
    return this.$_.keys(newAutomationValues).length
      ? this.$store.dispatch(`data/${DataModules.BRANDS}/update`, {
          id: this.brandId,
          data: newAutomationValues
        })
      : Promise.resolve();
  }

  public updateInvoiceNumberSettings() {
    const newNumberValues = this.getNewValues([
      "number",
      "prefix",
      "credit_note_number",
      "credit_note_prefix"
    ]);
    return this.$_.keys(newNumberValues).length
      ? this.$store.dispatch(
          `data/${DataModules.BRANDS}/updateInvoiceNumberAndPrefix`,
          {
            id: this.brandId,
            data: newNumberValues
          }
        )
      : Promise.resolve();
  }

  public getNewValues(values: string[]) {
    const newValues: any = this.$_.omitBy(this.form, (v, k) => {
      return this.formClone[k] === v;
    });
    return this.$_.pick(newValues, values);
  }

  public get getInvoiceNumber() {
    return this.form.number.toString().length < 7
      ? "0".repeat(7 - this.form.number.toString().length) + this.form.number
      : this.form.number;
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.formClone);
  }

  @Watch("formIsChanged")
  public onFormIsChanged(isChanged) {
    this.$emit("predefinedSettingsAreChanged", isChanged);
  }

  @Watch("isLoading", { immediate: true })
  public onIsLoadingChange() {
    this.$emit("predefinedSettingsAreLoading", this.isLoading);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: this.dataModule,
      scope: this.savedBrandScope,
      vm: this
    });
  }
}
